<template>
  <div class="space-y-3">
    <p class="m-0 text-xs text-gray-500">Confirmed {{ formatDate(changes.confirmedAt) }} by {{ changes.confirmedBy }}</p>

    <div
      v-if="summary.decreased > 0"
      class="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-800"
    >
      <span>⚠ {{ summary.decreased }} capacity decrease{{ summary.decreased === 1 ? '' : 's' }} — review with the partner</span>
      <button v-if="filter !== 'decreased'" type="button" class="text-xs font-medium underline" @click="filter = 'decreased'">Show only these</button>
    </div>

    <div class="flex flex-wrap gap-2">
      <button
        v-for="pill in pills"
        :key="pill.value"
        type="button"
        class="rounded-full border px-3 py-1 text-xs font-medium"
        :class="filter === pill.value ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'"
        @click="filter = pill.value"
      >
        {{ pill.label }} {{ pill.count }}
      </button>
    </div>

    <p v-if="visibleGroups.length === 0" class="m-0 py-6 text-center text-sm text-gray-500">No {{ activePillLabel ? `${activePillLabel} ` : '' }}changes in this import.</p>

    <div v-else class="max-h-[32rem] overflow-x-auto overflow-y-auto">
      <table class="min-w-full text-sm">
        <thead>
          <tr>
            <th class="table-th sticky top-0 z-10 bg-gray-50">Type</th>
            <th class="table-th sticky top-0 z-10 bg-gray-50">Week</th>
            <th class="table-th sticky top-0 z-10 bg-gray-50 text-right">Before</th>
            <th class="table-th sticky top-0 z-10 bg-gray-50 text-right">After</th>
            <th class="table-th sticky top-0 z-10 bg-gray-50 text-right">Δ</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100">
          <template v-for="group in visibleGroups" :key="group.vendorId">
            <tr>
              <td colspan="5" class="px-4 pb-1 pt-3 text-sm font-semibold text-gray-900">
                {{ group.vendorName }}
                <span class="font-normal text-gray-500">
                  · {{ group.rows.length }} change{{ group.rows.length === 1 ? '' : 's' }}
                  <template v-if="group.net !== 0">· net {{ signed(group.net) }}</template>
                </span>
              </td>
            </tr>
            <tr v-for="row in group.rows" :key="rowKey(row)" :class="{ 'opacity-40': row.status === 'kept' || row.status === 'unchanged' }">
              <td class="px-4 py-1.5">{{ row.terminationCode }}</td>
              <td class="px-4 py-1.5 tabular-nums">{{ row.weekStart }}</td>
              <td class="px-4 py-1.5 text-right tabular-nums" :title="snapshotTitle(row.change)">{{ format(row.before) }}</td>
              <td class="px-4 py-1.5 text-right tabular-nums" :class="valueClass(row)" :title="snapshotTitle(row.change)">{{ format(row.after) }}</td>
              <td class="px-4 py-1.5 text-right font-medium tabular-nums" :class="valueClass(row)">{{ deltaLabel(row) }}</td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue';

  import type { AppliedAllocationChange, AppliedImportChanges } from '@/types/capacityImport';
  import type { DiffFilter, ImportDiffRow, ImportDiffVendorGroup } from '@/utils/importDiffRows';
  import { groupByVendor, matchesFilter, summarize, toDiffRows } from '@/utils/importDiffRows';

  const props = defineProps<{ changes: AppliedImportChanges }>();

  const numberFormat = new Intl.NumberFormat();
  const filter = ref<DiffFilter>('all');

  // In practice `loadDetail` flips `detailLoading` before assigning `detail`, so
  // ImportHistoryPanel's v-else-if chain unmounts this component on every record switch and a
  // fresh instance mounts with filter already at "all" — this watcher never actually fires.
  // Kept as defensive insurance for a future call site that keeps one instance mounted across
  // records, where a filter left on "decreased" would otherwise make the next import look empty.
  watch(
    () => props.changes,
    () => {
      filter.value = 'all';
    }
  );

  const rows = computed<ImportDiffRow[]>(() => toDiffRows(props.changes));
  const summary = computed(() => summarize(rows.value));

  const pills = computed<{ value: DiffFilter; label: string; count: number }[]>(() =>
    [
      { value: 'all' as const, label: 'All', count: summary.value.total },
      { value: 'decreased' as const, label: 'Decreases', count: summary.value.decreased },
      { value: 'increased' as const, label: 'Increases', count: summary.value.increased },
      { value: 'created' as const, label: 'New', count: summary.value.created },
      { value: 'kept' as const, label: 'Kept', count: summary.value.kept },
      { value: 'unchanged' as const, label: 'Unchanged', count: summary.value.unchanged },
    ].filter((pill) => pill.value === 'all' || pill.count > 0)
  );

  // Pill labels ("Decreases", "New") read fine on a button but not inlined into a sentence, so
  // the empty-state message uses its own adjective per filter — "all" yields '' so the sentence
  // reads "No changes in this import." instead of the ungrammatical "No all changes...".
  const emptyStateFilterLabels: Record<DiffFilter, string> = {
    all: '',
    decreased: 'decreased',
    increased: 'increased',
    created: 'new',
    kept: 'kept',
    unchanged: 'unchanged',
  };

  const activePillLabel = computed(() => emptyStateFilterLabels[filter.value]);

  const visibleGroups = computed<ImportDiffVendorGroup[]>(() => groupByVendor(rows.value.filter((row) => matchesFilter(row, filter.value))));

  function rowKey(row: ImportDiffRow): string {
    return `${row.vendorId}:${row.terminationTypeId}:${row.weekStart}`;
  }

  function format(value: number | null): string {
    return value === null ? '—' : numberFormat.format(value);
  }

  function signed(value: number): string {
    return `${value > 0 ? '+' : value < 0 ? '−' : ''}${numberFormat.format(Math.abs(value))}`;
  }

  function deltaLabel(row: ImportDiffRow): string {
    if (row.status === 'created') return 'new';
    if (row.status === 'kept') return 'kept';
    if (row.delta === null || row.delta === 0) return '—';
    return signed(row.delta);
  }

  function valueClass(row: ImportDiffRow): string {
    if (row.status === 'decreased') return 'text-red-700';
    if (row.status === 'increased') return 'text-green-700';
    if (row.status === 'created') return 'text-blue-700';
    return 'text-gray-500';
  }

  // The other snapshot fields live in the tooltip rather than more columns.
  function snapshotTitle(change: AppliedAllocationChange): string {
    const part = (label: string, before: number | null | undefined, after: number | null | undefined): string => `${label}: ${format(before ?? null)} → ${format(after ?? null)}`;
    return [
      part('capacity', change.before?.capacity, change.after?.capacity),
      part('line qty', change.before?.lineQty, change.after?.lineQty),
      part('input', change.before?.vendorInput, change.after?.vendorInput),
      part('factor', change.before?.capacityFactor, change.after?.capacityFactor),
    ].join('\n');
  }

  function formatDate(iso: string): string {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  }
</script>
